import { useLayoutEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { usePublishedHero } from 'hooks/usePublishedHero';
import HeroBackground from 'components/HeroBackground';
import HeroScrollRail from 'components/HeroScrollRail';
import PageIntroBand from 'components/PageIntroBand';
import ExpandableFooter from 'components/ExpandableFooter';
import SiteThemeControl from 'components/SiteThemeControl';
import { applySiteTheme, useSiteTheme, useThemePreference } from 'components/SiteTheme';
import { FlowButton } from 'components/ui/flow-button';
import {
  PAGE_HERO_CONTAINER_CLASS, PAGE_HERO_EYEBROW_CLASS,
  PAGE_HERO_H1_CLASS, PAGE_HERO_BODY_CLASS, PAGE_HERO_CTA_ROW_CLASS,
} from 'components/SiteHeroCtas';
import BookingDialog from 'components/paperwork/BookingDialog';
import PaperworkChooser from 'components/paperwork/PaperworkChooser';
import PaperworkFinder from 'components/resources/PaperworkFinder';
import { ARRANGEMENTS, INTRO, PACKAGES, STEPS, readSelection, selectionSearch } from 'components/paperwork/content';
import { EQUIPMENT, STATE_EDITIONS } from 'data/paperworkVariants';
import PaperworkOfferPrice from 'components/paperwork/PaperworkOfferPrice';
import { usePackagePriceDetails, usePackagePrices } from 'lib/paperworkPrices';
import { usePaperworkContent } from 'lib/paperworkContent';
// Keep shared popup/commerce compatibility styles unchanged. New rules are page-scoped.
import './PaperworkExperience.css';
import './PaperworkRedesign.css';

const PAPERWORK_MARQUEE_ITEMS = [
  'Driver files', 'Safety follow-up', 'Freight paperwork', 'Operating procedures',
  'W-2 employees', 'Owner-operators', 'Equipment', 'State editions',
];
const INTRODUCTION = [
  "Select paperwork for W-2 employees, owner-operators or both, then choose an equipment type and state edition. One package covers one working arrangement, one equipment type and one state. Selecting both buys two separate packages, with separate files, at twice the full package price.",
  "For government, UPS, pharmaceutical or other specialist freight, we discuss the additional paperwork and whether Standard or Premium fits your work. Additional state editions and any available discount can be discussed at the meeting. A free 20-minute introduction is optional before purchase.",
];
const OFFER_SCOPE = "Standard’s ISO 9001 quality management system sets out processes and responsibilities and explains how to review and improve them. Premium adds ISO 14001 environmental management and ISO 45001 occupational health and safety management, addressing environmental responsibilities and workplace hazards and risks. Your company implements and maintains the systems; paperwork does not provide ISO certification. Pay one $499 review deposit toward your package price for Standard or Premium, or for Basic with both arrangements. Standard and Premium include six hours of paperwork-review work; related review meetings are free. Preparation follows review, agreed scope and confirmed full payment. To have the deposit deducted once, place your remaining-balance order through the payment link within 30 days after we receive the deposit. You can request a deposit refund within 24 hours after payment receipt. After that window, if you decide not to continue, contact us to use the deposit for one Basic package for your selected state and chosen arrangement; a second Basic package costs extra. If Magellan causes a delay, you may choose a refund or continue the work. Extra training, meetings and work outside the included scope are quoted separately.";

export function PaperworkLegacyRedirect({ packageKey }) {
  const { packageId } = useParams();
  const { search } = useLocation();
  const value = packageKey || packageId;
  const valid = PACKAGES.some(item => item.id === value);
  const query = new URLSearchParams(search);
  const selection = valid
    ? { tier: value, arrangement: query.get('arrangement'), equipment: query.get('equipment'), state: query.get('state') }
    : { stage: 'compare' };
  return <Navigate replace to={{ pathname: '/paperwork', search: selectionSearch(search, selection) }} />;
}

function PaperworkHeroMedia() {
  const media = usePublishedHero('paperworkHero');
  // Admin owns the image. Loading, empty and failed selections retain the shared hero color.
  return <HeroBackground imageUrl={media.imageUrl} imageAlt={media.imageAlt} />;
}

export default function PaperworkPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const prices = usePackagePrices();
  const offers = usePackagePriceDetails();
  const packageContent = usePaperworkContent();
  const packages = packageContent.data?.packages || PACKAGES.map(({ id, name }) => ({ id, name }));
  const sharedTheme = useSiteTheme();
  const fallbackTheme = useThemePreference(!sharedTheme);
  const { preference, theme, select } = sharedTheme || fallbackTheme;
  const selection = readSelection(location.search);
  const query = new URLSearchParams(location.search);
  const chooserOpen = selection.stage !== 'arrival' && query.get('chooser') !== 'closed';
  const [entryStep, setEntryStep] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingContext, setBookingContext] = useState('');
  const openerRef = useRef(null);
  const compareRef = useRef(null);
  const bookingHandoff = useRef(false);
  const bookingOpenerRef = useRef(null);

  useLayoutEffect(() => {
    if (!sharedTheme) applySiteTheme(theme);
  }, [sharedTheme, theme]);

  function changeSelection(next) {
    const nextQuery = new URLSearchParams(selectionSearch(location.search, next));
    nextQuery.delete('chooser');
    navigate({ pathname: '/paperwork', search: nextQuery.toString() ? '?' + nextQuery : '' }, { preventScrollReset: true });
  }
  function openChooser(event, tier) {
    openerRef.current = event.currentTarget;
    setEntryStep(tier ? 1 : 0);
    changeSelection(tier
      ? tier === selection.tier ? selection : { tier }
      : selection.tier ? selection : { stage: 'compare' });
  }
  function setChooserOpen(open) {
    if (open) {
      changeSelection(selection.tier ? selection : { stage: 'compare' });
      return;
    }
    const nextQuery = new URLSearchParams(location.search);
    nextQuery.set('chooser', 'closed');
    navigate({ pathname: '/paperwork', search: '?' + nextQuery }, { preventScrollReset: true });
    setEntryStep(null);
    requestAnimationFrame(() => {
      if (!bookingHandoff.current) (openerRef.current || compareRef.current)?.focus({ preventScroll: true });
    });
  }
  function book(context, opener) {
    bookingOpenerRef.current = opener || document.activeElement;
    const current = context || {
      pkg: packages.find(item => item.id === selection.tier),
      arrangement: ARRANGEMENTS.find(item => item.id === selection.arrangement),
      equipment: EQUIPMENT.find(item => item.id === selection.equipment),
      stateEdition: STATE_EDITIONS.find(item => item.code === selection.state),
    };
    const selectedContext = [current.pkg?.name, current.arrangement?.label, current.equipment?.label, current.stateEdition?.name].filter(Boolean).join(' / ');
    bookingHandoff.current = true;
    setBookingContext(selectedContext || '');
    setBookingOpen(true);
  }

  return <main className="paperwork-redesign min-h-screen bg-canvas" data-theme={theme} data-testid="paperwork-experience">
    {!sharedTheme && <SiteThemeControl preference={preference} select={select} />}
    <section id="paperwork-hero" data-testid="paperwork-hero" data-theme-surface="dark"
      className="site-image-hero relative flex min-h-[100svh] w-full items-center overflow-hidden md:h-screen md:min-h-[680px] pw-page-hero"
      aria-labelledby="pw-hero-title">
      <PaperworkHeroMedia />
      <div className="absolute inset-0 grain-overlay" aria-hidden="true" />
      <div data-hero-text-parallax="true" className={PAGE_HERO_CONTAINER_CLASS}>
        <div className="max-w-4xl">
          <p className={PAGE_HERO_EYEBROW_CLASS + ' visible'}>Paperwork</p>
          <h1 id="pw-hero-title" className={PAGE_HERO_H1_CLASS + ' visible'}>Paperwork built around<br className="hidden sm:block" />{' '}<em className="shimmer-text">your trucking operation.</em></h1>
          <div className={PAGE_HERO_BODY_CLASS + ' visible'}>{INTRO.map(text => <p key={text}>{text}</p>)}</div>
          <div className={PAGE_HERO_CTA_ROW_CLASS + ' visible'}>
            <FlowButton ref={compareRef} onClick={event => openChooser(event)}>Compare paperwork packages</FlowButton>
            <FlowButton onClick={() => book()}>Book your free 20-minute meeting</FlowButton>
          </div>
        </div>
      </div>
      <HeroScrollRail targetId="paperwork-introduction" />
    </section>

    <div data-testid="paperwork-moving-strip" className="topic-rail overflow-hidden border-y border-ink/10 bg-surface">
      <div className="marquee-container"><div className="marquee-track">
        {[...PAPERWORK_MARQUEE_ITEMS, ...PAPERWORK_MARQUEE_ITEMS].map((item, index) => <span key={`${item}-${index}`}
          aria-hidden={index >= PAPERWORK_MARQUEE_ITEMS.length ? true : undefined}
          className="inline-flex items-center gap-3 px-9 font-body text-[9px] uppercase tracking-[0.44em] text-copy/24 whitespace-nowrap">
          {item}<span className="h-1 w-1 rotate-45 bg-gold/45" />
        </span>)}
      </div></div>
    </div>

    <div id="paperwork-introduction">
      <PageIntroBand testId="paperwork-page-intro" label="Paperwork" title="Start with how your company operates." backgroundText="PAPERWORK" paragraphs={INTRODUCTION} />
    </div>

    <section id="paperwork-packages" className="pw-page-section" aria-labelledby="pw-packages-title">
      <div className="pw-page-shell">
        <p className="pw-page-label">01 / Paperwork packages</p>
        <div className="pw-page-section-heading">
          <h2 id="pw-packages-title">Choose the right level<br /> <em className="shimmer-text">of paperwork.</em></h2>
          <p>Select a package, then choose your working arrangement, equipment and state edition.</p>
        </div>
        <PaperworkFinder />
        {packageContent.status !== 'ready' && <div className="pw-page-copy" role="status">
          <p>{packageContent.status === 'error' ? 'Package information is unavailable. Please try again.' : 'Loading package information…'}</p>
          {packageContent.status === 'error' && <FlowButton onClick={packageContent.retry}>Retry package information</FlowButton>}
        </div>}
        <div className="pw-page-packages">
          {packages.map((item, index) => <article key={item.id} className="pw-page-package">
            <div className="pw-page-package-top"><span className="pw-page-number">0{index + 1}</span></div>
            <h3>{item.name}</h3>
            <p className="pw-page-package-title">{item.title || '\u00a0'}</p>
            {item.description && <p className="pw-page-package-description">{item.description}</p>}
            {item.description && <details className="pw-page-package-mobile-details"><summary>Package details<Plus size={15} aria-hidden="true" /></summary><p>{item.description}</p></details>}
            <div className="pw-page-package-footer">
              <div className="pw-page-price"><PaperworkOfferPrice offer={offers[item.id]} minor={prices[item.id]} layout="card" /></div>
              <FlowButton onClick={event => openChooser(event, item.id)} aria-label={'Select ' + item.name}>Select {item.name}</FlowButton>
            </div>
          </article>)}
        </div>
      </div>
    </section>

    <section className="pw-page-section pw-page-section-tint" aria-labelledby="pw-process-title">
      <div className="pw-page-shell">
        <p className="pw-page-label">02 / The process</p>
        <h2 id="pw-process-title">From choosing your package<br /> <em className="shimmer-text">to using your paperwork.</em></h2>
        <ol className="pw-page-steps">{STEPS.map((step, index) => <li key={step.title}>
          <span className="pw-page-number" aria-hidden="true">0{index + 1}</span>
          <div><h3>{step.title}</h3><p>{step.body}</p></div>
        </li>)}</ol>
      </div>
    </section>

    <section id="paperwork-samples" className="pw-page-section" aria-labelledby="pw-samples-title">
      <div className="pw-page-shell pw-page-editorial">
        <div><p className="pw-page-label">03 / Sample documents</p><h2 id="pw-samples-title">Explore free resources<br /> <em className="shimmer-text">and document previews.</em></h2></div>
        <div className="pw-page-copy"><p>Visit Free Resources under Services for document previews and free reference materials for your trucking operation.</p><p>Use previews to assess the format and level of detail you need. Your selected edition's document list and the scope agreed with you confirm what your package includes.</p><FlowButton to="/resources">View previews and free documents</FlowButton></div>
      </div>
    </section>

    <section className="pw-page-section pw-page-section-tint" aria-labelledby="pw-existing-title">
      <div className="pw-page-shell">
        <div className="pw-page-editorial">
          <div><p className="pw-page-label">04 / Your existing paperwork</p><h2 id="pw-existing-title">Already have<br /> <em className="shimmer-text">company paperwork?</em></h2></div>
          <div className="pw-page-copy"><p>Your existing documents help us see what needs adding or changing. Every package includes a review after confirmed payment, followed by a written report and meeting. We explain what supports your operation, what is missing and whether Standard or Premium is a better fit. Specialist freight may call for additional documents; you receive a quote for changes before deciding to proceed.</p><FlowButton onClick={() => book()}>Book your free 20-minute meeting</FlowButton></div>
        </div>
        <div className="pw-page-scope" aria-labelledby="pw-scope-title"><h3 id="pw-scope-title">Understand the ISO systems, scope and deposit terms.</h3><p>{OFFER_SCOPE}</p></div>
      </div>
    </section>

    <ExpandableFooter />
    <PaperworkChooser open={chooserOpen} onOpenChange={setChooserOpen} initialSelection={selection}
      initialStep={entryStep} search={location.search} onSelectionChange={changeSelection} onBook={book} theme={theme} />
    <BookingDialog open={bookingOpen} onOpenChange={open => { bookingHandoff.current = open; setBookingOpen(open); }}
      onCloseAutoFocus={event => {
        if (bookingOpenerRef.current?.isConnected) {
          event.preventDefault();
          bookingOpenerRef.current.focus({ preventScroll: true });
        }
      }} theme={theme} selection={bookingContext} />
  </main>;
}
